import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';
import Icon from '../Shared/Icons';
import AnnouncementsWidget from '../Shared/AnnouncementsWidget';
import { PROGRAMS } from '../../config/programs';
import { fetchMembers, fetchReports } from '../../utils/programData';
import { isoWeekKey, weekLabel, daysLeftInWeek } from '../../utils/weekUtils';

export default function StaffHome() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ members: 0, reports: 0, pending: 0 });
  const [recentReports, setRecentReports] = useState([]);
  const [coverage, setCoverage] = useState(null); // { missing, total, daysLeft }
  const [perProgram, setPerProgram] = useState([]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  // ── Kila kitu sasa kinatoka kwenye programu ─────────────────────────
  async function loadAll() {
    const wk = isoWeekKey();
    try {
      const rows = [];
      let totalMembers = 0;
      let totalDone = 0;

      for (const p of PROGRAMS) {
        const members = await fetchMembers(p.id);
        const week = await fetchReports({ programId: p.id, kind: 'individual', weekKey: wk });
        totalMembers += members.length;
        totalDone += week.length;
        rows.push({
          program: p,
          members: members.length,
          done: week.length,
          pending: Math.max(0, members.length - week.length),
        });
      }
      setPerProgram(rows);
      setCoverage({
        missing: Math.max(0, totalMembers - totalDone),
        total: totalMembers,
        daysLeft: daysLeftInWeek(),
      });

      const mine = await fetchReports({ authorId: currentUser.uid });
      setStats({
        members: totalMembers,
        reports: mine.length,
        pending: Math.max(0, totalMembers - totalDone),
      });
      setRecentReports(mine.slice(0, 4));
    } catch (err) {
      console.error(err);
    }
  }

  const quickActions = [
    {
      icon: 'projects',
      label: 'Fungua Programu',
      sub: 'Chess, Msaada, Usa River YDC',
      path: '/programs',
      color: '#14452F',
    },
    {
      icon: 'report',
      label: 'Ripoti za Wiki',
      sub: 'Angalia nani bado hajaandikiwa',
      path: '/programs',
      color: '#4527A0',
    },
    {
      icon: 'user',
      label: 'Ripoti Yangu ya Wiki',
      sub: 'Changamoto, mafanikio na mipango',
      path: '/staff/weekly',
      color: '#E65100',
    },
  ];

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        {/* HEADER */}
        <div style={styles.hero}>
          <div style={styles.heroInner}>
            <div style={styles.heroTitle}>
              Karibu tena 👋
            </div>
            <div style={styles.heroSub}>
              Wiki ya sasa: {weekLabel()} · siku {daysLeftInWeek()} zimebaki
            </div>
          </div>
          <div style={styles.heroBadge}></div>
        </div>

        {/* STATS */}
        <div style={styles.statsGrid}>
          {[
            { emoji: '', value: stats.members, label: 'Wanachama wote', color: '#14452F' },
            { emoji: '', value: stats.pending, label: 'Bado wiki hii', color: '#E65100' },
            { emoji: '', value: stats.reports, label: 'Ripoti zangu', color: '#4527A0' },
          ].map((s, i) => (
            <div key={i} style={styles.statCard}>
              <div style={{
                ...styles.statBar,
                background: `linear-gradient(90deg, ${s.color}, ${s.color}99)`
              }} />
              <div style={styles.statEmoji}>{s.emoji}</div>
              <div style={styles.statVal}>{s.value}</div>
              <div style={styles.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        <AnnouncementsWidget limitTo={3} />

        {/* KILA PROGRAMU */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Programu Zangu</div>
          {perProgram.map(row => (
            <div
              key={row.program.id}
              onClick={() => navigate(`/programs/${row.program.id}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '13px 0', borderTop: '1px solid #F3F1EB', cursor: 'pointer',
              }}
            >
              <div style={{
                width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
                background: `${row.program.color}15`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={row.program.icon} size={17} color={row.program.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#1A1A1A' }}>
                  {row.program.name}
                </div>
                <div style={{ fontSize: '11.5px', color: '#6B7280', marginTop: '3px' }}>
                  Wanachama {row.members} · ripoti {row.done} wiki hii
                </div>
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '99px',
                color: row.pending ? '#D97706' : '#059669',
                background: row.pending ? '#FFFBEB' : '#ECFDF5',
                whiteSpace: 'nowrap',
              }}>
                {row.pending ? `${row.pending} bado` : 'Kamili'}
              </span>
            </div>
          ))}
        </div>

        {/* MONTHLY REPORT COVERAGE REMINDER */}
        {coverage && coverage.missing > 0 && (
          <div style={{
            ...styles.coverageBanner,
            ...(coverage.daysLeft <= 5 ? styles.coverageBannerUrgent : {})
          }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={styles.coverageTitle}>
                {coverage.daysLeft <= 2
                  ? `Siku ${coverage.daysLeft} zimebaki kumaliza wiki!`
                  : 'Ripoti za Wiki Hii'}
              </div>
              <div style={styles.coverageText}>
                Watu {coverage.missing} kati ya {coverage.total} bado hawajaandikiwa ripoti wiki hii.
              </div>
            </div>
            <button
              style={styles.coverageBtn}
              onClick={() => navigate('/programs')}
            >
              Angalia Waliobaki
            </button>
          </div>
        )}

        {/* QUICK ACTIONS */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Vitendo vya Haraka</div>
          <div style={styles.actionsGrid}>
            {quickActions.map((a, i) => (
              <div
                key={i}
                style={styles.actionCard}
                onClick={() => navigate(a.path)}
              >
                <div style={{
                  ...styles.actionIcon,
                  background: `linear-gradient(135deg, ${a.color}, ${a.color}99)`
                }}>
                  <Icon name={a.icon} size={24} color={a.color} />
                </div>
                <div style={styles.actionLabel}>{a.label}</div>
                <div style={styles.actionSub}>{a.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RECENT REPORTS */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>
            <span>Ripoti Zangu za Karibuni</span>
          </div>
          {recentReports.length === 0 ? (
            <div style={styles.empty}>
              Bado hujaandika ripoti. Fungua programu kuanza.
            </div>
          ) : (
            recentReports.map((r) => (
              <div key={r.id} style={styles.reportItem}>
                <div style={styles.reportIcon}></div>
                <div style={styles.reportInfo}>
                  <div style={styles.reportTitle}>
                    {r.memberName || r.clubName || r.eventName || 'Ripoti ya wiki'}
                  </div>
                  <div style={styles.reportMeta}>
                    {r.kind === 'individual' ? 'Ripoti binafsi'
                      : r.kind === 'session' ? 'Ripoti ya session'
                      : 'Ripoti ya staff'} ·{' '}
                    {r.weekLabel || r.weekKey || ''}
                  </div>
                </div>
                <span style={styles.reportBadge}>
                  {r.contributions?.length ? `+${r.contributions.length}` : 'OK'}
                </span>
              </div>
            ))
          )}
        </div>

        {/* NEXT SESSION REMINDER */}
        <div style={styles.sessionCard}>
          <div style={styles.sessionLeft}>
            <div style={styles.sessionIcon}></div>
            <div>
              <div style={styles.sessionTitle}>
                Next Saturday Session
              </div>
              <div style={styles.sessionSub}>
                Life Skills & Chess · 2:00 PM every Saturday
              </div>
            </div>
          </div>
          <div style={styles.sessionBadge}>Upcoming</div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  coverageBanner: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', background: '#FFF8E9', border: '1.5px solid #F0DEAE', borderRadius: '14px', padding: '16px 20px', marginBottom: '18px' },
  coverageBannerUrgent: { background: '#FFEBEE', border: '1.5px solid #FFCDD2' },
  coverageTitle: { fontSize: '14px', fontWeight: '800', color: '#7A5B00' },
  coverageText: { fontSize: '12.5px', color: '#6B7280', marginTop: '2px' },
  coverageBtn: { background: '#14452F', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '9px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  hero: {
    background: 'linear-gradient(135deg, #0B3524 0%, #175239 45%, #2d6e22 100%)',
    borderRadius: '20px',
    padding: '26px',
    marginBottom: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
  },
  heroTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '24px',
    color: '#fff',
    marginBottom: '6px',
  },
  heroSub: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
  },
  heroBadge: {
    fontSize: '60px',
    opacity: 0.15,
    position: 'absolute',
    right: '24px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '14px',
    marginBottom: '22px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px 18px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    position: 'relative',
    overflow: 'hidden',
  },
  statBar: {
    height: '3px',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    borderRadius: '3px 3px 0 0',
  },
  statEmoji: { fontSize: '24px', marginBottom: '10px' },
  statVal: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '30px',
    lineHeight: 1,
    marginBottom: '3px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  statLbl: { fontSize: '12px', color: '#6B7280' },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '20px',
  },
  panelTitle: {
    fontSize: '13.5px',
    fontWeight: '600',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '14px',
  },
  actionCard: {
    borderRadius: '14px',
    padding: '18px 16px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actionIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    marginBottom: '12px',
    color: '#fff',
  },
  actionLabel: {
    fontSize: '13.5px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  actionSub: {
    fontSize: '11.5px',
    color: '#6B7280',
    lineHeight: 1.4,
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
  reportItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  reportIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    flexShrink: 0,
  },
  reportInfo: { flex: 1 },
  reportTitle: {
    fontSize: '13.5px',
    fontWeight: '500',
  },
  reportMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginTop: '1px',
  },
  reportBadge: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    background: '#EAF3EC',
    color: '#14452F',
    fontWeight: '500',
  },
  sessionCard: {
    background: 'linear-gradient(135deg, #0B3524, #175239)',
    borderRadius: '16px',
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  sessionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  sessionIcon: {
    fontSize: '28px',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sessionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
  },
  sessionSub: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.55)',
    marginTop: '3px',
  },
  sessionBadge: {
    background: 'rgba(245,124,0,0.2)',
    color: '#FFA726',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    border: '1px solid rgba(245,124,0,0.3)',
  },
};